import { userStorage } from "$root/functional-areas/user/userStorage";
import WidgetsMapper, { type WidgetMapperProps } from "$root/widgets-architecture/layout/WidgetsMapper";
import { type ColumnMetadata, spawnColumnChooser } from "$root/ui-lib/table/extra/ColumnChooser";
import useResizeObserver from "@react-hook/resize-observer";
import { type FC, useCallback, useImperativeHandle, useLayoutEffect, useMemo, useState } from "react";
import { widgetLabels } from "./widget-labels";
import { defaultWidgetsGridConfig } from "./default-grids-config";
import { storedGridSchema, type StoredWidgetEntry, type WidgetEntry } from "./types";
import "./WidgetsGrid.css";
import type { ImperativeHandleRefProps } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { filterMap, sortByScore, tryJsonParse } from "$root/utils";
import { clamp } from "@mdotm/shadcn/legacy/utils";

export type WidgetGridHandles = {
	openOrderPanel(): void;
};

export type WidgetsGridProps = {
	gridName: keyof typeof defaultWidgetsGridConfig;
} & ImperativeHandleRefProps<WidgetGridHandles>;

function getStoredWidgets(gridName: string): StoredWidgetEntry[] | null {
	return tryJsonParse(userStorage.get(`widget-grid:${gridName}`) ?? "", null, (x) => storedGridSchema.parse(x));
}

export function sanitizeWidgets(stored: StoredWidgetEntry[] | null, defaults: WidgetEntry[]): StoredWidgetEntry[] {
	if (!stored) {
		return defaults.map((w) => ({
			i: w.i,
			span: w.span,
		}));
	}
	const sanitized = filterMap(stored, (w) => {
		const def = defaults.find((d) => d.i === w.i);
		if (!def) return [];
		const span = clamp(w.span ?? def.span, def.minSpan, def.maxSpan) as 1 | 2 | 3;
		return [{ ...w, span }];
	});

	// Append default widgets introduced after the user's layout was stored, so new
	// widgets show up at the end without needing to bump the grid version.
	const storedIds = new Set(stored.map((w) => w.i));
	const newWidgets = defaults.filter((def) => !storedIds.has(def.i)).map((def) => ({ i: def.i, span: def.span }));

	return [...sanitized, ...newWidgets];
}

const WidgetsGrid: FC<WidgetsGridProps> = (props) => <WidgetGridInner key={props.gridName} {...props} />;

const WidgetGridInner: FC<WidgetsGridProps> = ({ gridName, handleRef }) => {
	const defaultWidgets = defaultWidgetsGridConfig[gridName].widgets;
	const [storedWidgets, setStoredWidgets] = useState<StoredWidgetEntry[] | null>(() => getStoredWidgets(gridName));
	const widgets = useMemo(() => sanitizeWidgets(storedWidgets, defaultWidgets), [storedWidgets, defaultWidgets]);

	const saveOrder = useCallback(
		(newWidgets: StoredWidgetEntry[]) => {
			userStorage.set(`widget-grid:${gridName}`, JSON.stringify(newWidgets));
			setStoredWidgets(newWidgets);
		},
		[gridName],
	);

	const refs = useUnsafeUpdatedRef({ defaultWidgets, storedWidgets, saveOrder });
	const openOrderPanel = useCallback(() => {
		const { defaultWidgets, storedWidgets, saveOrder } = refs.current;
		const columns: ColumnMetadata<WidgetMapperProps["widgetName"]>[] = sortByScore(
			defaultWidgets.map((w) => ({
				id: w.i,
				label: widgetLabels[w.i],
				checked: !storedWidgets?.find((x) => x.i === w.i)?.hidden,
			})),
			(col) => {
				const idx = storedWidgets?.findIndex((w) => w.i === col.id);
				return idx == null || idx === -1 ? defaultWidgets.length : idx;
			},
		);

		spawnColumnChooser({
			title: "Page Settings",
			description: "Manage widgets and display options",
			columns,
			onChange(newColumns) {
				saveOrder(
					newColumns.map((col) => {
						const user = storedWidgets?.find((w) => w.i === col.id);
						const system = defaultWidgets.find((w) => w.i === col.id)!;
						return { i: col.id, hidden: !col.checked, span: user?.span ?? system.span };
					}),
				);
			},
		});
	}, [refs]);

	useImperativeHandle(handleRef, () => ({
		openOrderPanel,
	}));

	const visibleWidgets = widgets.filter(({ hidden }) => !hidden);

	const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);

	const syncRowHeights = useCallback((grid: HTMLDivElement) => {
		const allWidgets = Array.from(grid.querySelectorAll<HTMLElement>(".widget"));

		const rowMap = new Map<number, HTMLElement[]>();
		for (const w of allWidgets) {
			const top = Math.round(w.getBoundingClientRect().top);
			rowMap.set(top, [...(rowMap.get(top) ?? []), w]);
		}

		const tracks = [...rowMap.values()].map((rowWidgets) => {
			let max: number | "none" | undefined;
			for (const w of rowWidgets) {
				const raw = w.dataset.maxHeight;
				if (raw === "none") {
					max = "none";
					break;
				}
				if (raw !== undefined) {
					const n = parseFloat(raw);
					if (!Number.isNaN(n)) {
						max = Math.max((max as number | undefined) ?? 0, n);
					}
				}
			}

			// Apply per-widget inline max-height so CSS `.widget { max-height: 512px }`
			// doesn't cap widgets in rows with a larger (or no) constraint.
			for (const w of rowWidgets) {
				if (max === "none") {
					w.style.maxHeight = "none";
				} else if (max !== undefined) {
					w.style.maxHeight = `${max}px`;
				} else {
					w.style.maxHeight = ""; // clear inline override, let CSS 512px default apply
				}
			}

			if (max === "none" || max === undefined) return "auto";
			return `minmax(240px, ${max}px)`;
		});

		grid.style.gridTemplateRows = tracks.join(" ");
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: visibleWidgets triggers re-sync when widgets are shown/hidden
	useLayoutEffect(() => {
		if (gridEl) syncRowHeights(gridEl);
	}, [gridEl, visibleWidgets, syncRowHeights]);

	useResizeObserver(gridEl, () => {
		if (gridEl) syncRowHeights(gridEl);
	});

	return (
		/* The wrapper exists only to be the size container the grid's column count queries — see
		   WidgetsGrid.css. Layout-neutral: it's a plain block, like the grid it replaces in flow. */
		<div className="widgets-grid-container">
			<div ref={setGridEl} data-widget-grid-name={gridName} className="widgets-grid">
				{visibleWidgets.map(({ i, span }) => (
					<WidgetsMapper key={i} widgetName={i} span={span} />
				))}
			</div>
		</div>
	);
};

export default WidgetsGrid;
